<template>
  <div class="checkm2-container">
    <div class="content-section">
      <h1>Average Amino Acid Identity Analysis (EzAAI)</h1>
      
      <div class="description">
        <h2>About EzAAI</h2>
        <p>EzAAI is a comprehensive tool for calculating Average Amino Acid Identity (AAI) between genomes. It provides a streamlined workflow for genome comparison and analysis.</p>
        
        <p class="additional-info">
          EzAAI offers three main functions for genome analysis and comparison:
        </p>
        <ul class="model-list">
          <li><strong>Extract:</strong> Extract protein sequences from genomic data using Prodigal</li>
          <li><strong>Calculate:</strong> Compute AAI values between genomes using DIAMOND</li>
          <li><strong>Cluster:</strong> Group genomes based on AAI values using hierarchical clustering</li>
        </ul>
        
        <el-alert
          title="Processing Time Note"
          type="info"
          :closable="false"
          show-icon
        >
          <p>Analysis may take several minutes to hours, depending on genome size and selected function.</p>
        </el-alert>

        <div class="features">
          <h3>Key Features:</h3>
          <ul>
            <li>Supports multiple genome formats (.fa, .fasta, .fna)</li>
            <li>Fast and accurate protein sequence extraction</li>
            <li>Efficient AAI calculation with multi-threading</li>
            <li>Flexible clustering methods for genome grouping</li>
            <li>Comprehensive result visualization</li>
            <li>User-friendly interface and workflow</li>
          </ul>
        </div>
      </div>

      <!-- Module Selection -->
      <div class="module-selection">
        <h2>Select Analysis Module</h2>
        <el-radio-group v-model="selectedModule" @change="handleModuleChange">
          <el-radio-button label="extract">Extract</el-radio-button>
          <el-radio-button label="calculate">Calculate</el-radio-button>
          <el-radio-button label="cluster">Cluster</el-radio-button>
        </el-radio-group>
      </div>

      <div class="upload-section">
        <h2>{{ uploadSectionTitle }}</h2>
        <p class="upload-info">{{ uploadDescription }}</p>
        
        <el-upload
          class="upload-demo"
          drag
          :action="uploadUrl"
          :data="{ module: selectedModule }"
          :on-success="selectedModule === 'calculate' ? handleProteinSuccess : (selectedModule === 'cluster' ? handleMatrixSuccess : handleGenomeSuccess)"
          :on-error="handleError"
          :before-upload="selectedModule === 'calculate' ? beforeUploadProtein : (selectedModule === 'cluster' ? beforeUploadMatrix : beforeUpload)"
          :on-progress="handleProgress"
          :multiple="selectedModule !== 'cluster'"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Drop files here or <em>click to upload</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              Supported formats: {{ 
                selectedModule === 'calculate' ? '.faa (protein sequences)' : 
                selectedModule === 'cluster' ? '.tsv, .txt (distance matrix)' : 
                '.fa, .fasta, .fna (genome sequences)' 
              }}
            </div>
          </template>
        </el-upload>

        <!-- Upload Progress Display -->
        <div v-if="analyzing" class="progress-section">
          <el-progress 
            :percentage="analysisProgress" 
            :status="analysisProgress === 100 ? 'success' : ''"
            :format="progressFormat"
          />
          <p class="progress-text">{{ progressText }}</p>
        </div>

        <!-- File List -->
        <div v-if="uploadedFiles.length > 0" class="file-list">
          <h3>Uploaded Files</h3>
          <el-table :data="uploadedFiles" style="width: 100%" border>
            <el-table-column prop="name" label="File Name" />
            <el-table-column prop="size" label="Size" width="120" />
            <el-table-column prop="status" label="Status" width="100">
              <template #default="scope">
                <el-tag :type="scope.row.status === 'valid' ? 'success' : 'danger'">
                  {{ scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="100">
              <template #default="scope">
                <el-button 
                  type="danger" 
                  circle
                  @click="removeFile(scope.row)"
                >
                  <el-icon><Delete /></el-icon>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Analysis Parameters -->
      <div v-if="showParameters" class="parameters-section">
        <h2>Analysis Parameters</h2>
        
        <!-- Extract Parameters -->
        <el-form v-if="selectedModule === 'extract'" :model="parameters.extract" label-width="180px">
          <el-form-item label="Genetic Code">
            <el-select v-model="parameters.extract.genetic_code">
              <el-option label="Standard / Bacterial (11)" value="11" />
              <el-option label="Alternative Yeast Nuclear (12)" value="12" />
              <el-option label="Ascidian Mitochondrial (13)" value="13" />
              <el-option label="Alternative Flatworm Mitochondrial (14)" value="14" />
            </el-select>
          </el-form-item>
          <el-form-item label="Minimum Length">
            <el-input-number 
              v-model="parameters.extract.min_length" 
              :min="30" 
              :max="200"
              :step="10"
            />
          </el-form-item>
          <el-form-item label="Procedure">
            <el-select v-model="parameters.extract.procedure">
              <el-option label="Prodigal" value="prodigal" />
              <el-option label="MetaGeneMark" value="metagenemark" />
            </el-select>
          </el-form-item>
        </el-form>

        <!-- Calculate Parameters -->
        <el-form v-if="selectedModule === 'calculate'" :model="parameters.calculate" label-width="180px">
          <el-form-item label="Calculation Mode">
            <el-select v-model="parameters.calculate.mode">
              <el-option label="Pairwise Comparison" value="pairwise" />
              <el-option label="Matrix Calculation" value="matrix" />
            </el-select>
          </el-form-item>
          <el-form-item label="Identity Threshold (%)">
            <el-input-number 
              v-model="parameters.calculate.identity_threshold" 
              :min="0" 
              :max="100"
              :step="5"
            />
          </el-form-item>
          <el-form-item label="Coverage Threshold (%)">
            <el-input-number 
              v-model="parameters.calculate.coverage_threshold" 
              :min="0" 
              :max="100"
              :step="5"
            />
          </el-form-item>
          <el-form-item label="Threads">
            <el-input-number 
              v-model="parameters.calculate.threads" 
              :min="1" 
              :max="32"
              :step="1"
            />
          </el-form-item>
          <el-form-item label="E-value">
            <el-select v-model="parameters.calculate.e_value">
              <el-option label="1e-3" :value="1e-3" />
              <el-option label="1e-5" :value="1e-5" />
              <el-option label="1e-10" :value="1e-10" />
              <el-option label="1e-15" :value="1e-15" />
            </el-select>
          </el-form-item>
          <el-form-item label="Fast Mode">
            <el-switch v-model="parameters.calculate.fast_mode" />
          </el-form-item>
        </el-form>

        <!-- Cluster Parameters -->
        <el-form v-if="selectedModule === 'cluster'" :model="parameters.cluster" label-width="180px">
          <el-form-item label="Clustering Method">
            <el-select v-model="parameters.cluster.method">
              <el-option label="Single Linkage (Minimum)" value="single" />
              <el-option label="Complete Linkage (Maximum)" value="complete" />
              <el-option label="Average Linkage (UPGMA)" value="average" />
              <el-option label="Ward's Method" value="ward" />
            </el-select>
          </el-form-item>
          <el-form-item label="Distance Metric">
            <el-select v-model="parameters.cluster.metric">
              <el-option label="Euclidean" value="euclidean" />
              <el-option label="Manhattan" value="manhattan" />
              <el-option label="Cosine" value="cosine" />
            </el-select>
          </el-form-item>
        </el-form>

        <!-- Submit Button -->
        <div class="submit-section">
          <el-button 
            type="primary" 
            @click="submitAnalysis"
            :loading="analyzing"
            :disabled="!canStartAnalysis"
            size="large"
          >
            Start Analysis
          </el-button>
        </div>
      </div>

      <!-- Results Display -->
      <div v-if="results" class="results-section">
        <div class="results-header">
          <h2>Analysis Results</h2>
          <div class="download-buttons" v-if="selectedModule === 'calculate' && results.matrix">
            <el-button 
              type="primary" 
              @click="downloadMatrix('similarity')"
              :loading="downloading === 'similarity'"
            >
              Download Similarity Matrix (AAI)
            </el-button>
            <el-button 
              type="success" 
              @click="downloadMatrix('distance')"
              :loading="downloading === 'distance'"
            >
              Download Distance Matrix
            </el-button>
            <el-button 
              type="warning" 
              @click="downloadMatrix('raw')"
              :loading="downloading === 'raw'"
            >
              Download Raw Distance Matrix
            </el-button>
            <el-button 
              type="primary" 
              @click="downloadMatrix('cluster')"
              :loading="downloading === 'cluster'"
            >
              Download Cluster Format Matrix
            </el-button>
          </div>
          <el-button 
            type="primary" 
            @click="downloadResults"
            :loading="downloading === true"
            v-if="selectedModule === 'cluster'"
          >
            Download Clustering Results
          </el-button>
        </div>

        <!-- Extract Results -->
        <template v-if="selectedModule === 'extract' && results?.extract">
          <el-table
            :data="results.extract"
            style="width: 100%"
            border
          >
            <el-table-column prop="genome" label="Genome File" />
            <el-table-column prop="proteins" label="Proteins Extracted" />
            <el-table-column prop="avgLength" label="Average Length" />
            <el-table-column label="Actions" width="120">
              <template #default="scope">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="downloadFile(scope.row)"
                >
                  Download
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </template>

        <!-- Calculate Results -->
        <template v-if="selectedModule === 'calculate' && results?.results">
          <el-table
            :data="results.results"
            style="width: 100%"
            border
          >
            <el-table-column prop="query_file" label="Query Genome" />
            <el-table-column prop="ref_file" label="Reference Genome" />
            <el-table-column 
              prop="aai" 
              label="AAI Value"
              :formatter="(row) => `${row.aai.toFixed(2)}%`"
            />
            <el-table-column prop="matched_proteins" label="Matched Proteins" />
            <el-table-column 
              prop="coverage" 
              label="Coverage"
              :formatter="(row) => `${(row.coverage * 100).toFixed(2)}%`"
            />
          </el-table>
        </template>

        <!-- Cluster Results -->
        <template v-if="selectedModule === 'cluster'">
          <div v-if="analyzing" class="analyzing-message">
            <el-alert
              title="Performing Cluster Analysis"
              type="info"
              description="Please wait, results will be displayed automatically when analysis is complete"
              show-icon
              :closable="false"
            />
          </div>
          
          <div v-else-if="error" class="error-message">
            <el-alert
              :title="error"
              type="error"
              :description="errorDetails"
              show-icon
              @close="clearError"
            />
          </div>
          
          <div v-else-if="results?.status === 'success'" class="success-message">
            <el-alert
              title="Cluster Analysis Complete"
              type="success"
              description="Cluster tree file has been generated. Click the download button to get the result file"
              show-icon
              :closable="false"
            />
          </div>
        </template>

        <!-- Matrix Results -->
        <template v-if="selectedModule === 'calculate' && results?.matrix">
          <el-table
            :data="results.data"
            style="width: 100%"
            border
          >
            <el-table-column
              v-for="(col, index) in results.data[0]"
              :key="index"
              :prop="index.toString()"
              :label="index === 0 ? '' : results.data[0][index]"
              :formatter="(row, column) => formatMatrixValue(row[column.property])"
            />
          </el-table>
        </template>
      </div>
    </div>

    <!-- Citation Section -->
    <div class="citation-section">
      <h3>Citation and License</h3>
      <div class="citation-content">
        <p class="citation-text">
          This tool integrates EzAAI, developed by Zhou et al. If you use this tool in your research, please cite:
        </p>
        <div class="citation-box">
          <p class="paper-citation">
            <strong>Primary Citation:</strong><br>
            Kim, D., Park, S. & Chun, J. Introducing EzAAI: a pipeline for high throughput calculations of prokaryotic average amino acid identity.
            <em>J. Microbiol 59 </em>,  476-480 (2021). <a href="https://link.springer.com/article/10.1007/s12275-021-1154-0" target="_blank">https://link.springer.com/article/10.1007/s12275-021-1154-0</a>
          </p>
          <p class="repo-citation">
            <strong>Software Repository:</strong><br>
            EzAAI GitHub Repository: <a href="https://github.com/endixk/EzAAI" target="_blank">https://github.com/endixk/EzAAI</a>
          </p>
        </div>
        <div class="license-info">
          <p class="license-text">
            EzAAI is licensed under the MIT License.
          </p>
          <p class="usage-note">
            This is an integration of EzAAI and is not officially affiliated with the EzAAI project. For the official implementation and latest updates, please visit the EzAAI GitHub repository.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { UploadFilled, Delete } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

export default {
  name: 'EzAAITool',
  components: {
    UploadFilled,
    Delete
  },
  setup() {
    const selectedModule = ref('extract')
    const analyzing = ref(false)
    const analysisProgress = ref(0)
    const progressText = ref('')
    const results = ref(null)
    const downloading = ref(false)
    const error = ref('')
    const errorDetails = ref('')
    const uploadedFiles = ref([])
    const jobId = ref('')

    const showParameters = computed(() => {
      return selectedModule.value !== ''
    })

    const parameters = ref({
      extract: {
        genetic_code: '11',
        min_length: 50,
        procedure: 'prodigal'
      },
      calculate: {
        mode: 'pairwise',
        identity_threshold: 30,
        coverage_threshold: 70,
        threads: 4,
        e_value: 1e-10,
        fast_mode: true
      },
      cluster: {
        method: 'single',
        metric: 'euclidean'
      }
    })

    const uploadUrl = '/api/tools/ezaai/upload'

    const handleModuleChange = () => {
      uploadedFiles.value = []
      results.value = null
      error.value = ''
      errorDetails.value = ''
      analysisProgress.value = 0
    }

    const beforeUpload = (file) => {
      const isValidType = ['fa', 'fasta', 'fna'].includes(
        file.name.split('.').pop().toLowerCase()
      )
      const isLt500M = file.size / 1024 / 1024 < 500

      if (!isValidType) {
        ElMessage.error('Please upload a FASTA format file!')
        return false
      }
      if (!isLt500M) {
        ElMessage.error('File size cannot exceed 500MB!')
        return false
      }
      return true
    }

    const beforeUploadProtein = (file) => {
      const isValidType = file.name.toLowerCase().endsWith('.faa')
      const isLt100M = file.size / 1024 / 1024 < 100

      if (!isValidType) {
        ElMessage.error('Please upload a protein sequence file (.faa)!')
        return false
      }
      if (!isLt100M) {
        ElMessage.error('File size cannot exceed 100MB!')
        return false
      }
      return true
    }

    const beforeUploadMatrix = (file) => {
      const isValidType = ['tsv', 'txt'].includes(
        file.name.split('.').pop().toLowerCase()
      )
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isValidType) {
        ElMessage.error('Please upload a TSV or TXT file!')
        return false
      }
      if (!isLt10M) {
        ElMessage.error('File size cannot exceed 10MB!')
        return false
      }
      return true
    }

    const handleGenomeSuccess = (response, uploadFile) => {
      console.log('Genome upload response:', response)
      console.log('Upload file object:', uploadFile)

      try {
        if (!response) {
          throw new Error('Server response is empty')
        }

        // 检查是否已存在相同文件名的文件
        const existingFileIndex = uploadedFiles.value.findIndex(
          f => f.name === (uploadFile?.name || response.filename || response.name)
        )

        const fileInfo = {
          name: uploadFile?.name || response.filename || response.name,
          size: uploadFile ? `${(uploadFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size',
          status: 'valid',
          path: response.path || response.file_path || response.data?.path
        }

        if (!fileInfo.name || !fileInfo.path) {
          throw new Error('File information is incomplete')
        }

        // 如果文件已存在，替换它
        if (existingFileIndex !== -1) {
          uploadedFiles.value.splice(existingFileIndex, 1)
        }

        // 添加新文件
        uploadedFiles.value.push(fileInfo)
        ElMessage.success('Genome file uploaded successfully')
      } catch (error) {
        console.error('处理上传响应时出错:', error)
        handleError(error, uploadFile)
      }
    }

    const handleProteinSuccess = (response, uploadFile) => {
      console.log('处理蛋白质文件上传响应:', response)
      console.log('上传文件对象:', uploadFile)

      try {
        if (!response) {
          throw new Error('Server response is empty')
        }

        // Check if response contains error message
        if (response.error) {
          throw new Error(response.error)
        }

        // Check response format
        if (!response.path && !response.file_path && (!response.data || !response.data.path)) {
          console.error('Invalid response format:', response)
          throw new Error('Invalid file path from server')
        }

        const filePath = response.path || response.file_path || response.data?.path
        // Handle path encoding issues
        const decodedPath = decodeURIComponent(filePath)

        // Check if file with same name exists
        const existingFileIndex = uploadedFiles.value.findIndex(
          f => f.name === (uploadFile?.name || response.filename || response.name)
        )

        const fileInfo = {
          name: uploadFile?.name || response.filename || response.name,
          size: uploadFile ? `${(uploadFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size',
          status: 'valid',
          path: decodedPath,
          module: 'calculate',
          type: 'protein'
        }

        if (!fileInfo.name || !fileInfo.path) {
          console.error('Incomplete file information:', fileInfo)
          throw new Error('Incomplete file information')
        }

        // Replace if file exists
        if (existingFileIndex !== -1) {
          uploadedFiles.value.splice(existingFileIndex, 1)
        }

        // Add new file
        uploadedFiles.value.push(fileInfo)
        ElMessage.success('Protein file uploaded successfully')
      } catch (error) {
        console.error('Error processing upload response:', error)
        handleError(error, uploadFile)
        // Remove failed file
        const failedFileIndex = uploadedFiles.value.findIndex(
          f => f.name === uploadFile?.name
        )
        if (failedFileIndex !== -1) {
          uploadedFiles.value.splice(failedFileIndex, 1)
        }
      }
    }

    const handleMatrixSuccess = (response, uploadFile) => {
      console.log('Matrix upload response:', response)
      console.log('Upload file object:', uploadFile)

      try {
        if (!response) {
          throw new Error('Server response is empty')
        }

        const fileInfo = {
          name: uploadFile?.name || response.filename || response.name,
          size: uploadFile ? `${(uploadFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size',
          status: 'valid',
          path: response.path || response.file_path || response.data?.path
        }

        if (!fileInfo.name || !fileInfo.path) {
          throw new Error('File information is incomplete')
        }

        // 矩阵文件始终只保留一个
        uploadedFiles.value = [fileInfo]
        ElMessage.success('Matrix file uploaded successfully')
      } catch (error) {
        console.error('处理上传响应时出错:', error)
        handleError(error, uploadFile)
      }
    }

    const handleError = (error, file) => {
      console.error('Upload error:', error)
      let errorMessage = 'File upload failed'
      
      if (error.response) {
        try {
          const responseData = typeof error.response === 'string' 
            ? JSON.parse(error.response) 
            : error.response.data || error.response
          console.log('Error response data:', responseData)
          errorMessage = responseData.message || responseData.error || error.message || errorMessage
        } catch (e) {
          console.error('Failed to parse error response:', e)
          errorMessage = error.message || errorMessage
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      ElMessage.error(errorMessage)
      
      if (file) {
        const fileInfo = {
          name: file.name || 'Unknown file',
          size: file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size',
          status: 'error',
          error: errorMessage,
          module: 'calculate',
          type: 'protein'
        }
        
        const existingFileIndex = uploadedFiles.value.findIndex(f => f.name === file.name)
        if (existingFileIndex !== -1) {
          uploadedFiles.value.splice(existingFileIndex, 1)
        }
        
        uploadedFiles.value.push(fileInfo)
      }
    }

    const handleProgress = (event, file) => {
      if (!file) return
      
      const existingFile = uploadedFiles.value.find(f => f.name === file.name)
      const progress = event?.percent || 0
      
      if (existingFile) {
        existingFile.progress = progress
        existingFile.status = progress === 100 ? 'processing' : 'uploading'
      } else {
        uploadedFiles.value.push({
          name: file.name,
          size: file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size',
          status: 'uploading',
          progress
        })
      }
    }

    const removeFile = (file) => {
      const index = uploadedFiles.value.findIndex(f => f.name === file.name)
      if (index !== -1) {
        uploadedFiles.value.splice(index, 1)
      }
    }

    const canStartAnalysis = computed(() => {
      if (analyzing.value) return false
      
      switch (selectedModule.value) {
        case 'extract':
          return uploadedFiles.value.length > 0
        case 'calculate':
          if (parameters.value.calculate.mode === 'pairwise') {
            return uploadedFiles.value.length === 2
          } else {
            return uploadedFiles.value.length >= 2
          }
        case 'cluster':
          return uploadedFiles.value.length === 1
        default:
          return false
      }
    })

    const submitAnalysis = async () => {
      if (!canStartAnalysis.value) {
        ElMessage.warning('Please upload the required files')
        return
      }

      analyzing.value = true
      error.value = ''
      errorDetails.value = ''
      
      try {
        let formData = {
          function: selectedModule.value,
          files: uploadedFiles.value.map(file => file.path.replace(/\\/g, '/'))
        }

        // 根据不同模块添加对应的参数
        if (selectedModule.value === 'extract') {
          formData = {
            ...formData,
            genetic_code: parameters.value.extract.genetic_code,
            min_length: parameters.value.extract.min_length,
            procedure: parameters.value.extract.procedure
          }
        } else if (selectedModule.value === 'calculate') {
          formData = {
            ...formData,
            mode: parameters.value.calculate.mode,
            identity_threshold: parameters.value.calculate.identity_threshold,
            coverage_threshold: parameters.value.calculate.coverage_threshold,
            threads: parameters.value.calculate.threads,
            e_value: parameters.value.calculate.e_value,
            fast_mode: parameters.value.calculate.fast_mode
          }
        } else if (selectedModule.value === 'cluster') {
          formData = {
            ...formData,
            method: parameters.value.cluster.method,
            metric: parameters.value.cluster.metric
          }
        }

        console.log('Sending analysis request:', JSON.stringify(formData, null, 2))
        
        const response = await fetch('/api/tools/ezaai/analyze', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Analysis request failed')
        }

        const result = await response.json()
        console.log('Analysis result:', result)

        // 更新结果显示
        if (selectedModule.value === 'calculate') {
          results.value = result
          jobId.value = result.job_id
          
          // 如果是矩阵模式，显示矩阵结果
          if (parameters.value.calculate.mode === 'matrix' && result.matrix) {
            results.value = {
              ...result,
              matrix: true,
              data: result.matrix
            }
          }
        } else if (selectedModule.value === 'cluster') {
          results.value = result
          jobId.value = result.jobId  // 保存jobId
        } else if (selectedModule.value === 'extract') {
          results.value = {
            extract: result.data.results.map(item => ({
              genome: item.filename,
              proteins: item.proteins,
              avgLength: item.avgLength,
              output: item.path,
              job_id: result.data.job_id
            }))
          }
          jobId.value = result.data.job_id
        }
        
        ElMessage.success('Analysis completed')
        
      } catch (err) {
        console.error('Analysis error:', err)
        error.value = 'Analysis failed'
        errorDetails.value = err.message
        ElMessage.error(`Analysis failed: ${err.message}`)
      } finally {
        analyzing.value = false
      }
    }

    const downloadFile = async (file) => {
      if (!file || !file.output) {
        ElMessage.warning('No file available for download')
        return
      }

      try {
        const currentJobId = file.job_id || jobId.value
        if (!currentJobId) {
          throw new Error('Unable to get task ID')
        }

        const fileName = file.output.split('\\').pop().split('/').pop()
        const fileUrl = `/api/tools/ezaai/download/${currentJobId}/${fileName}`
        
        console.log('Preparing to download file:', {
          fileName,
          fileUrl,
          jobId: currentJobId
        })
        
        const response = await fetch(fileUrl)
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'File does not exist or has been deleted')
        }
        
        const blob = await response.blob()
        if (blob.size === 0) {
          throw new Error('File is empty')
        }
        
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        
        ElMessage.success('Download successful')
      } catch (error) {
        console.error('Download error:', error)
        ElMessage.error(error.message || 'Download failed, please try again')
      }
    }

    const downloadResults = async () => {
      if (!results.value?.status) {
        ElMessage.warning('No matrix file available for download')
        return
      }
      
      if (downloading.value === true) {
        return  // 如果正在下载，直接返回
      }
      
      downloading.value = true
      try {
        let downloadUrl
        let fileName
        
        // 获取当前的jobId
        const currentJobId = results.value.jobId || results.value.job_id || jobId.value
        
        if (!currentJobId) {
          console.error('Result object:', results.value)
          throw new Error('Unable to get task ID')
        }
        
        console.log('Current task ID:', currentJobId)
        console.log('Current result object:', results.value)
        
        if (selectedModule.value === 'cluster') {
          // 使用后端返回的实际文件路径
          const resultFile = results.value.data.result_file
          if (!resultFile) {
            throw new Error('Result file path not found')
          }
          fileName = resultFile.split('\\').pop().split('/').pop()
          downloadUrl = `/api/tools/ezaai/download/${currentJobId}/${fileName}`
        } else {
          // 对于其他模块使用通用下载路径
          downloadUrl = `/api/tools/ezaai/download/${currentJobId}`
          fileName = `${selectedModule.value}_results.tsv`
        }
        
        console.log('Preparing to download file:', {
          downloadUrl,
          fileName,
          moduleType: selectedModule.value,
          resultFile: results.value.data?.result_file
        })
        
        const response = await fetch(downloadUrl)
        console.log('Download response status:', response.status, response.statusText)
        
        if (!response.ok) {
          let errorMessage
          try {
            const errorData = await response.json()
            console.error('Download error response:', errorData)
            errorMessage = errorData.error || 'Download failed'
          } catch (e) {
            console.error('Parsing error response failed:', e)
            errorMessage = `Download failed (${response.status})`
          }
          throw new Error(errorMessage)
        }
        
        const blob = await response.blob()
        console.log('Downloaded file size:', blob.size)
        
        if (blob.size === 0) {
          throw new Error('Result file is empty')
        }
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        ElMessage.success('Download successful')
      } catch (error) {
        console.error('Download error:', error)
        ElMessage.error(error.message || 'Download failed, please try again')
      } finally {
        downloading.value = false  // 确保在任何情况下都重置下载状态
      }
    }

    const downloadMatrix = async (type) => {
      if (!results.value?.status) {
        ElMessage.warning('No matrix file available for download')
        return
      }

      downloading.value = type
      try {
        let filePath
        switch (type) {
          case 'similarity':
            filePath = results.value.similarity_matrix_path
            break
          case 'distance':
            filePath = results.value.distance_matrix_path
            break
          case 'raw':
            filePath = results.value.raw_distance_matrix_path
            break
          case 'cluster':
            filePath = results.value.cluster_matrix_path
            break
          default:
            throw new Error('Unknown matrix type')
        }

        if (!filePath) {
          console.error('Matrix file path:', {
            type,
            results: results.value
          })
          throw new Error('Matrix file path does not exist')
        }

        // 只获取文件名，不使用完整路径
        const filename = filePath.split('\\').pop().split('/').pop()
        const downloadUrl = `/api/tools/ezaai/download/${results.value.job_id}/${filename}`
        
        console.log('Downloading matrix:', {
          type,
          filename,
          downloadUrl,
          filePath
        })

        const response = await fetch(downloadUrl)
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Download failed')
        }

        const blob = await response.blob()
        if (blob.size === 0) {
          throw new Error('File is empty')
        }

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        ElMessage.success('Download successful')
      } catch (error) {
        console.error('Download error:', error)
        ElMessage.error(error.message || 'Download failed, please try again')
      } finally {
        downloading.value = ''
      }
    }

    const uploadSectionTitle = computed(() => {
      switch (selectedModule.value) {
        case 'extract':
          return 'Upload Genome Files'
        case 'calculate':
          return 'Upload Protein Files'
        case 'cluster':
          return 'Upload Distance Matrix'
        default:
          return 'Upload Files'
      }
    })

    const uploadDescription = computed(() => {
      switch (selectedModule.value) {
        case 'extract':
          return 'Upload genome files in FASTA format to extract protein sequences.'
        case 'calculate':
          return 'Upload protein sequence files (.faa) to calculate AAI values.(Make sure the .faa file comes from Extract module)'
        case 'cluster':
          return 'Upload a distance matrix file to perform hierarchical clustering.(Make sure the .tsv file comes from Calculate module)'
        default:
          return 'Please select a module and upload corresponding files.'
      }
    })

    const progressFormat = (percentage) => {
      if (percentage === 100) {
        return 'Completed'
      }
      return `${percentage}%`
    }

    const formatMatrixValue = (value) => {
      if (typeof value === 'number') {
        return `${value.toFixed(2)}%`
      }
      return value
    }

    return {
      selectedModule,
      parameters,
      analyzing,
      analysisProgress,
      progressText,
      results,
      downloading,
      uploadUrl,
      uploadedFiles,
      error,
      errorDetails,
      handleModuleChange,
      beforeUpload,
      beforeUploadProtein,
      beforeUploadMatrix,
      handleGenomeSuccess,
      handleProteinSuccess,
      handleMatrixSuccess,
      handleError,
      handleProgress,
      removeFile,
      canStartAnalysis,
      submitAnalysis,
      downloadResults,
      downloadFile,
      downloadMatrix,
      Delete,
      showParameters,
      uploadSectionTitle,
      uploadDescription,
      progressFormat,
      formatMatrixValue
    }
  }
}
</script>

<style scoped>
.checkm2-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.features {
  margin-top: 20px;
}

.features h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.features ul {
  list-style-type: none;
  padding-left: 0;
}

.features li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.features li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.module-selection {
  margin: 20px 0;
  text-align: center;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.parameters-section {
  margin: 20px 0;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.submit-section {
  margin: 20px 0;
  text-align: center;
}

.results-section {
  margin-top: 30px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.results-header h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
}

.download-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload__tip) {
  color: #666;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
}

.citation-box a:hover {
  text-decoration: underline;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.additional-info {
  margin: 15px 0;
  color: #666;
  line-height: 1.6;
}

.model-list {
  list-style-type: none;
  padding-left: 20px;
  margin: 15px 0;
}

.model-list li {
  margin-bottom: 10px;
  position: relative;
  padding-left: 20px;
}

.model-list li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

.paper-citation {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
  line-height: 1.6;
}

.paper-citation em {
  font-style: italic;
}

.repo-citation {
  margin-bottom: 0;
  line-height: 1.6;
}

.citation-box strong {
  color: #2c3e50;
}

.citation-box a {
  word-break: break-all;
}

.analyzing-message,
.error-message,
.success-message {
  margin: 20px 0;
}

.file-list {
  margin-top: 20px;
}

.file-list h3 {
  color: #2c3e50;
  margin-bottom: 15px;
}
</style> 